import styled, { keyframes } from 'styled-components';

/* Marketplace buy→PO screens. Token-only colors (no raw hex). Numbers/prices/
   SKUs/PO-codes use .mono.tnum classes from GlobalStyles. */

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

/* ── Hero search band (page 01) ── */
export const StyledHero = styled.div`
  background: linear-gradient(135deg, var(--brand-ink) 0%, var(--brand-ink-2) 100%);
  border-radius: var(--r-xl);
  padding: var(--s-7);
  margin-bottom: var(--s-5);
  position: relative;
  overflow: hidden;
`;

export const StyledHeroEyebrow = styled.div`
  color: var(--brand-lime);
  position: relative;
  font-size: var(--t-xs);
  font-weight: var(--weight-semibold);
  letter-spacing: var(--tracking-eyebrow);
  text-transform: uppercase;
`;

export const StyledHeroTitle = styled.h2`
  margin: 8px 0 18px;
  color: var(--surface);
  font-size: var(--t-3xl);
  font-weight: 700;
  letter-spacing: -0.02em;
  position: relative;
`;

export const StyledHeroSearchRow = styled.form`
  display: flex;
  gap: 10px;
  position: relative;
  max-width: 720px;
`;

export const StyledHeroInputWrap = styled.div`
  flex: 1;
  position: relative;
  display: flex;
  align-items: center;

  svg {
    position: absolute;
    inset-inline-start: 14px;
    color: var(--muted);
    width: 18px;
    height: 18px;
    pointer-events: none;
  }

  input {
    width: 100%;
    box-sizing: border-box;
    padding: 13px 14px;
    padding-inline-start: 42px;
    height: 48px;
    border: 0;
    border-radius: var(--r-md);
    background: var(--surface);
    font-size: var(--t-base);
    font-family: inherit;
    outline: none;
    color: var(--ink);
  }
`;

export const StyledTrendingRow = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 14px;
  position: relative;
  flex-wrap: wrap;
  align-items: center;
`;

export const StyledTrendingLabel = styled.span`
  font-size: var(--t-xs);
  color: rgba(255, 255, 255, 0.5);
`;

export const StyledTrendingChip = styled.button`
  padding: 4px 12px;
  border-radius: var(--r-full);
  border: 1px solid rgba(255, 255, 255, 0.18);
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.8);
  cursor: pointer;
  font-family: inherit;
  font-size: var(--t-xs);
`;

/* ── Brands strip ── */
export const StyledStripHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--s-3);
`;

export const StyledClearBrand = styled.button`
  border: 0;
  background: transparent;
  color: var(--brand-green);
  font-weight: 600;
  font-size: var(--t-sm);
  cursor: pointer;
  font-family: inherit;
`;

export const StyledBrandGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(8, 1fr);
  gap: 10px;
  margin-bottom: var(--s-6);

  @media (max-width: 1100px) {
    grid-template-columns: repeat(4, 1fr);
  }
  @media (max-width: 560px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

export const StyledBrandButton = styled.button<{ $active?: boolean }>`
  padding: 14px 10px;
  border-radius: var(--r-lg);
  cursor: pointer;
  font-family: inherit;
  border: 1px solid ${({ $active }) => ($active ? 'var(--brand-green)' : 'var(--line)')};
  background: ${({ $active }) => ($active ? 'var(--success-soft)' : 'var(--surface)')};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  position: relative;
  transition: all var(--dur-fast) var(--ease-out);

  &:hover {
    border-color: var(--brand-green);
  }
`;

export const StyledBrandCheck = styled.span`
  position: absolute;
  top: 8px;
  inset-inline-end: 8px;
  color: var(--brand-green);
  display: flex;

  svg {
    width: 14px;
    height: 14px;
  }
`;

export const StyledBrandName = styled.div`
  font-weight: 700;
  font-size: var(--t-sm);
`;

export const StyledBrandMeta = styled.div`
  color: var(--muted);
  font-size: 11px;
`;

/* ── Category + sort toolbar ── */
export const StyledToolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: var(--s-4);
  flex-wrap: wrap;
`;

export const StyledCatPills = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  flex: 1;
`;

export const StyledCatPill = styled.button<{ $active?: boolean }>`
  padding: 8px 14px;
  border-radius: var(--r-full);
  cursor: pointer;
  font-family: inherit;
  border: 1px solid ${({ $active }) => ($active ? 'var(--brand-ink)' : 'var(--line)')};
  background: ${({ $active }) => ($active ? 'var(--brand-ink)' : 'var(--surface)')};
  color: ${({ $active }) => ($active ? 'var(--surface)' : 'var(--ink-2)')};
  font-size: var(--t-sm);
  font-weight: ${({ $active }) => ($active ? 600 : 500)};
`;

export const StyledSortWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const StyledSortSelect = styled.select`
  height: 38px;
  padding: 0 12px;
  border: 1px solid var(--line);
  border-radius: var(--r-md);
  background: var(--surface);
  font-family: inherit;
  font-size: var(--t-sm);
  color: var(--ink);
  outline: none;
`;

/* ── Product grid + card ── */
export const StyledGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: var(--s-4);
`;

export const StyledCardImage = styled.div<{ $hue: number }>`
  position: relative;
  aspect-ratio: 1 / 1;
  background: var(--paper-2);
  display: grid;
  place-items: center;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    padding: var(--s-4);
  }
`;

/* Honest image fallback: brand monogram + product glyph on the brand hue.
   Fills its parent panel; never a random stock photo. */
export const StyledImageFallback = styled.div<{ $hue: number; $scale?: number }>`
  width: 100%;
  height: 100%;
  display: grid;
  place-items: center;
  gap: ${({ $scale }) => 6 * ($scale ?? 1)}px;
  background: linear-gradient(
    135deg,
    oklch(0.96 0.03 ${({ $hue }) => $hue}) 0%,
    oklch(0.9 0.05 ${({ $hue }) => $hue}) 100%
  );
  color: oklch(0.4 0.12 ${({ $hue }) => $hue});

  span {
    font-weight: 800;
    letter-spacing: -0.02em;
    font-size: ${({ $scale }) => 34 * ($scale ?? 1)}px;
    line-height: 1;
  }
  svg {
    width: ${({ $scale }) => 20 * ($scale ?? 1)}px;
    height: ${({ $scale }) => 20 * ($scale ?? 1)}px;
    opacity: 0.55;
  }
`;

export const StyledBadgeTL = styled.span`
  position: absolute;
  top: 10px;
  inset-inline-start: 10px;
`;

export const StyledBadgeTR = styled.span`
  position: absolute;
  top: 10px;
  inset-inline-end: 10px;
`;

export const StyledCardBody = styled.div`
  padding: var(--s-4);
  display: flex;
  flex-direction: column;
  gap: 7px;
  flex: 1;
`;

export const StyledRatingRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 11px;
  color: var(--muted);
`;

export const StyledCardName = styled.div`
  font-weight: 600;
  font-size: var(--t-base);
  line-height: 1.3;
  min-height: 40px;
`;

export const StyledPriceLine = styled.div`
  display: flex;
  align-items: baseline;
  gap: 6px;
`;

export const StyledPriceBig = styled.span`
  font-size: var(--t-xl);
  font-weight: 700;
  color: var(--brand-green-ink);
`;

export const StyledPriceRange = styled.span`
  font-size: var(--t-xs);
  color: var(--muted);
`;

export const StyledMetaLine = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 11px;
  color: var(--muted);

  svg {
    width: 12px;
    height: 12px;
  }
`;

export const StyledStock = styled.div<{ $low?: boolean }>`
  font-size: 11px;
  font-weight: ${({ $low }) => ($low ? 700 : 500)};
  color: ${({ $low }) => ($low ? 'var(--warning-ink)' : 'var(--muted)')};
`;

export const StyledAddBtn = styled.button<{ $inCart?: boolean; $soldOut?: boolean }>`
  margin-top: 4px;
  height: 38px;
  border-radius: var(--r-md);
  cursor: ${({ $soldOut }) => ($soldOut ? 'not-allowed' : 'pointer')};
  font-family: inherit;
  font-weight: 600;
  font-size: var(--t-sm);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border: 1px solid
    ${({ $inCart, $soldOut }) =>
      $soldOut ? 'var(--line)' : $inCart ? 'var(--brand-green)' : 'var(--brand-ink)'};
  background: ${({ $inCart, $soldOut }) =>
    $soldOut ? 'var(--paper-2)' : $inCart ? 'var(--success-soft)' : 'var(--brand-ink)'};
  color: ${({ $inCart, $soldOut }) =>
    $soldOut ? 'var(--muted)' : $inCart ? 'var(--success-ink)' : 'var(--surface)'};

  svg {
    width: 15px;
    height: 15px;
  }
`;

/* ── Empty / skeleton / error ── */
export const StyledEmpty = styled.div`
  grid-column: 1 / -1;
  text-align: center;
  padding: var(--s-9) var(--s-4);
  color: var(--muted);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--s-3);
`;

export const StyledSkeletonCard = styled.div`
  border-radius: var(--r-lg);
  border: 1px solid var(--line);
  overflow: hidden;
  background: var(--surface);
`;

export const StyledSkeletonBlock = styled.div<{ $h: string; $w?: string }>`
  height: ${({ $h }) => $h};
  width: ${({ $w }) => $w ?? '100%'};
  margin: ${({ $w }) => ($w ? 'var(--s-2) var(--s-4)' : '0')};
  border-radius: ${({ $w }) => ($w ? 'var(--r-xs)' : '0')};
  background: linear-gradient(
    90deg,
    var(--paper) 25%,
    var(--paper-2) 50%,
    var(--paper) 75%
  );
  background-size: 200% 100%;
  @media (prefers-reduced-motion: no-preference) {
    animation: ${shimmer} 1.4s ease-in-out infinite;
  }
`;

export const StyledErrorState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--s-3);
  padding: var(--s-9) var(--s-4);
  color: var(--muted);
  text-align: center;
`;

/* ── Shared layout grids ── */
export const StyledOrderGrid = styled.div<{ $side?: string }>`
  display: grid;
  grid-template-columns: 1fr ${({ $side }) => $side ?? '360px'};
  gap: var(--s-5);
  align-items: start;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
`;

export const StyledStickyCol = styled.div`
  position: sticky;
  top: calc(var(--topbar-h) + 24px);
  display: flex;
  flex-direction: column;
  gap: var(--s-4);
`;

/* ── Breadcrumb ── */
export const StyledBreadcrumb = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: var(--s-4);
  flex-wrap: wrap;
`;

export const StyledBackChip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: var(--r-md);
  background: transparent;
  border: 1px solid var(--line);
  color: var(--muted);
  cursor: pointer;
  font-family: inherit;
  font-size: var(--t-sm);
  font-weight: 500;

  svg {
    width: 14px;
    height: 14px;
  }
`;

export const StyledCrumbSep = styled.span`
  color: var(--muted-2);
  display: flex;
  svg {
    width: 14px;
    height: 14px;
  }
`;

export const StyledCrumbMuted = styled.span`
  font-size: var(--t-sm);
  color: var(--muted);
`;

export const StyledCrumbCurrent = styled.span`
  font-size: var(--t-sm);
  font-weight: 600;
`;

/* ── Cart count pill (on the ink Cart button) ── */
export const StyledCountPill = styled.span`
  background: var(--brand-lime);
  color: var(--brand-ink);
  padding: 2px 7px;
  border-radius: var(--r-full);
  font-size: 11px;
  font-weight: 700;
  margin-inline-start: 2px;
`;
